import {
    createRestClient,
    RestClient,
} from '@superbet-group/web.lib.rest';
import { defaultHttpTimeoutMs } from '@core/constants';
import { getCmsUrl } from '@core/services/cms/utils';
import { CustomMarketGroup } from '@modules/eventGrid/components/MarketLayouts/MarketLayoutTypes';
import { BetBuilderMarketCategory } from '@sportOfferFederation/bet-builder/multi-event/types/BetBuilderMarketCategory';
import { Country } from '@superbet-group/web.types';
import { getConfig } from '@superbet-group/web.lib.config';
import { HomeComponentKey } from '@shared/sportsLayouts/homeComponentsList';
import { SportsComponentKey } from '@shared/sportsLayouts/sportsComponentsList';
import { getPlatformParameter } from '@src/federations/shared/cms/types/common';

export const NON_FINAL_MAX_ODDS = 3;

type MarketDetails = {
    id: number
    name?: string
};

type MarketConfig = number | MarketDetails;

type NonFinalSportConfig = Record<number, MarketConfig[]>;

export type NonFinalConfig = {
    default: NonFinalSportConfig
    overrides: Record<string, NonFinalSportConfig>
    middleLineMarkets: Record<number, number[]>
};

type PopularAccasSportConfig = Record<number, Accumulator[]>;

type Accumulator = { selections: AccumulatorSelection[], count: number };

type AccumulatorSelection = { eventId: number, outcomeId: number };

type PopularAccasSettings = Record<number, { home: number, away: number, draw?: number }>;

export type PopularAccasConfig = {
    [K in Country | 'settings']: K extends 'settings' ? PopularAccasSettings : PopularAccasSportConfig;
};

export type TopWinnersSelection = {
    eventName: string;
    selectionValue: string;
};

export type TopWinnersSuggestedSelection = {
    eventId: number;
    oddUuid: string;
};

type TopWinnersTicket = {
    id: string,
    ticketName: string;
    totalOdds: number;
    stake: number;
    payout: number;
    superBonusAmount: number;
    ticketSelections: TopWinnersSelection[];
    suggestedTicketSelections: TopWinnersSuggestedSelection[];
};

export type TopWinnersConfig = {
    lastUpdate: string;
    daily: TopWinnersTicket[],
    weekly: TopWinnersTicket[],
    monthly: TopWinnersTicket[],
};

export const SportsLayoutsPage = {
    HOME: 'home',
    SPORTS: 'sports',
} as const;

export type SportsLayoutsPage = typeof SportsLayoutsPage[keyof typeof SportsLayoutsPage];

export type PageComponentsMap = {
    [SportsLayoutsPage.HOME]: HomeComponentKey[];
    [SportsLayoutsPage.SPORTS]: SportsComponentKey[];
};

export type SportsLayoutsConfig<T extends SportsLayoutsPage> = {
    featureFlagKey: string;
    isDefault: boolean;
    components: PageComponentsMap[T];
};

class OfferPresentationService {
    private readonly restClient: RestClient = createRestClient(
        getCmsUrl({
            path: '',
        }),
        defaultHttpTimeoutMs,
    );

    public getMarketLayouts(): Promise<CustomMarketGroup[]> {
        return this.restClient.get('/mobile-widgets/67');
    }

    public getMarketCategories(): Promise<BetBuilderMarketCategory[]> {
        return this.restClient.get('/mobile-widgets/139');
    }

    public getNonFinalMarketsOnHomepage(): Promise<NonFinalConfig> {
        return this.restClient.get('/mobile-widgets/134');
    }

    public getPopularAccumulators(): Promise<PopularAccasConfig> {
        return this.restClient.get('/mobile-widgets/135');
    }

    public getTopWinners(): Promise<TopWinnersConfig> {
        const country = getConfig().countrySpecific.country;

        return this.restClient.get(`/top-winners/${country}`);
    }

    public getSportsLayouts(page: SportsLayoutsPage, target: Country):Promise<SportsLayoutsConfig<typeof page>[]> {
        const platform = getPlatformParameter();

        return this.restClient.get(`/sports-layouts/${page}/${platform}/${target}`);
    }
}

export default new OfferPresentationService();
